import Alert from "@mui/material/Alert";
import classNames from "classnames";
import * as React from "react";
import { Link } from "react-router-dom";

import { ViewAsOrgButton } from "@/shared/components";
import CopyToClipboardButton from "@/shared/components/CopyToClipboardButton";
import { ResourceView } from "@/shared/components/ResourceView";
import { OrganizationTier, type OrgRecord } from "@/shared/domain/orgs";
import { useListOrgs } from "@/shared/domain/orgs/hooks";
import {
  ColumnVisibility,
  DataTable,
  buildColumnsForPreferences,
  createColumnHelper,
  useTable,
  type ColDef,
  type ColumnPickerOption,
  type DataTableLoadingState,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";

import styles from "./AllOrgsViewer.module.css";

const columnHelper = createColumnHelper<OrgRecord>();

const COLUMNS: ColDef<OrgRecord>[] = [
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 2,
    minWidth: 160,
    cellClass: styles.nameCell,
  }),
  columnHelper.accessor("org_id", {
    headerName: "ID",
    flex: 2,
    minWidth: 220,
    cellClass: styles.idCell,
    cellRenderer: (params: ICellRendererParams<OrgRecord, string>) => {
      const id = params.value;
      if (!id) {
        return null;
      }
      return (
        <span className={styles.idCellInner}>
          {/*
            Links to the standalone org page (`/orgs/:orgId`). `stopPropagation`
            keeps the row's `onCellClicked` (which only previews the org in the
            side panel) from also firing, so the id is the one affordance that
            navigates away.
          */}
          <Link
            to={`/orgs/${id}`}
            className={styles.idLink}
            onClick={(event) => event.stopPropagation()}
          >
            {id}
          </Link>
          <CopyToClipboardButton
            text={id}
            eventName="CopyLinkClicked"
            eventProps={{ link: id }}
            className={styles.idCopyButton}
          />
        </span>
      );
    },
  }),
  columnHelper.accessor("tier", {
    headerName: "Tier",
    flex: 1,
    minWidth: 90,
    cellRenderer: (
      params: ICellRendererParams<OrgRecord, OrganizationTier>,
    ) => {
      const tier = params.value;
      if (!tier) {
        return null;
      }
      const isPremium = tier === OrganizationTier.Premium;
      return (
        <span
          className={classNames(styles.tierPill, {
            [styles.tierPillPremium]: isPremium,
            [styles.tierPillFree]: !isPremium,
          })}
        >
          {tier}
        </span>
      );
    },
  }),
  columnHelper.accessor("status", {
    headerName: "Status",
    flex: 1,
    minWidth: 100,
  }),
  columnHelper.accessor("created", {
    headerName: "Created",
    flex: 1,
    minWidth: 110,
    valueFormatter: (params) =>
      params.value ? params.value.toString().substring(0, 10) : "",
  }),
  {
    colId: "actions",
    headerName: "",
    width: 110,
    pinned: "right",
    sortable: false,
    resizable: false,
    suppressMovable: true,
    // `hide: false` overrides DataTable's `defaultColDef.hide: true` baseline
    // so the actions column is visible even though it's not toggled on
    // through the column-visibility picker.
    hide: false,
    lockVisible: true,
    cellClass: styles.actionsCell,
    cellRenderer: (params: ICellRendererParams<OrgRecord>) => {
      const org = params.data;
      if (!org) {
        return null;
      }
      return <ViewAsOrgButton org={org} size="small" />;
    },
  },
];

const ACTIONS_COLUMN_ID = "actions";

function colId<T>(col: ColDef<T>): string {
  return col.colId ?? col.field ?? "";
}

const COLUMN_PICKER_OPTIONS: ColumnPickerOption[] = COLUMNS.filter(
  (c) => colId(c) !== ACTIONS_COLUMN_ID,
).map((c) => ({
  id: colId(c),
  label: c.headerName ?? colId(c),
}));

// Toggleable columns shown by default. The actions column is rendered
// outside this system (it always shows via per-ColDef `hide: false`), so
// it must NOT appear here — otherwise the picker badge would count a
// column the picker doesn't list. `status` is available in the picker
// but hidden by default to keep the table focused on the columns admins
// actually scan for.
const HIDDEN_BY_DEFAULT_COLUMN_IDS = new Set(["status"]);
const DEFAULT_VISIBLE_COLUMN_IDS = COLUMN_PICKER_OPTIONS.filter(
  (c) => !HIDDEN_BY_DEFAULT_COLUMN_IDS.has(c.id),
).map((c) => c.id);

interface SummaryChipProps {
  label: string;
  count: number | undefined;
  variant?: "default" | "premium" | "total";
  /**
   * When provided, the chip renders as a toggle button driving the tier
   * filter. `active` reflects whether its tier is the one currently
   * filtered, both for styling and as the `aria-pressed` state.
   */
  onClick?: () => void;
  active?: boolean;
}

function SummaryChip({
  label,
  count,
  variant = "default",
  onClick,
  active = false,
}: SummaryChipProps) {
  const className = classNames(styles.summaryChip, {
    [styles.summaryChipPremium]: variant === "premium",
    [styles.summaryChipTotal]: variant === "total",
    [styles.summaryChipButton]: onClick !== undefined,
    [styles.summaryChipActive]: active,
  });

  const body = (
    <>
      <span>{label}</span>
      <span className={styles.summaryChipCount}>
        {count === undefined ? "…" : count}
      </span>
    </>
  );

  if (onClick === undefined) {
    return <span className={className}>{body}</span>;
  }

  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      aria-pressed={active}
    >
      {body}
    </button>
  );
}

interface AllOrgsViewerProps {
  /**
   * Fired when the admin clicks any cell in a row. The parent uses this
   * to drive the adjacent settings panel — we deliberately do NOT call
   * `setCurrentOrganization` here so browsing the table doesn't change
   * the admin's actual org context for the rest of the app.
   */
  onSelectOrg?: (org: OrgRecord) => void;
}

export function AllOrgsViewer({ onSelectOrg }: AllOrgsViewerProps = {}) {
  const table = useTable<OrgRecord>({
    getRowId: (row) => row.org_id,
    defaultVisibleColumnIds: DEFAULT_VISIBLE_COLUMN_IDS,
  });

  // The admin orgs table only filters by tier and name, so it manages those
  // two as plain local state rather than going through ResourceView's
  // structured-filter machinery. `tier` is driven by the summary chips
  // (exclusive toggle); `search` is the committed name query and `searchDraft`
  // is the live input value, promoted to `search` only on submit.
  const [tier, setTier] = React.useState<OrganizationTier | undefined>(
    undefined,
  );
  const [search, setSearch] = React.useState("");
  const [searchDraft, setSearchDraft] = React.useState("");

  const { resetCurrentPage } = table;

  // Clicking a tier chip toggles it: clicking the active tier clears the
  // filter, clicking the other tier switches to it. Either way the page
  // resets so the new result set starts from the first page.
  const handleTierToggle = React.useCallback(
    (clickedTier: OrganizationTier) => {
      setTier((prev) => (prev === clickedTier ? undefined : clickedTier));
      resetCurrentPage();
    },
    [resetCurrentPage],
  );

  const commitSearch = React.useCallback(
    (value: string) => {
      setSearch(value.trim());
      resetCurrentPage();
    },
    [resetCurrentPage],
  );

  const handleSearchSubmit = React.useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      commitSearch(searchDraft);
    },
    [searchDraft, commitSearch],
  );

  const handleSearchChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value;
      setSearchDraft(next);
      if (next === "") {
        // The native clear "x" (and delete-to-empty) fires onChange but never
        // submits the form, so commit the empty search immediately.
        commitSearch("");
      }
    },
    [commitSearch],
  );

  // DataTable applies `defaultColDef.hide: true`, so each visible column
  // must be flipped to `hide: false` at render time. The actions column
  // already carries `hide: false` on its ColDef so it shows regardless.
  // We also stamp `onCellClicked` onto every ColDef so a click on any
  // cell pages the parent panel; the in-cell Link and CopyToClipboard
  // button stop propagation (or navigate away) so they don't double-fire.
  const columns = React.useMemo<ColDef<OrgRecord>[]>(() => {
    const base = buildColumnsForPreferences<OrgRecord>({
      staticColumns: COLUMNS,
      dynamicColumns: [],
      preferenceOrder: DEFAULT_VISIBLE_COLUMN_IDS,
    });

    if (!onSelectOrg) {
      return base;
    }

    return base.map((col) => ({
      ...col,
      onCellClicked: (event) => {
        if (event.data) {
          onSelectOrg(event.data);
        }
      },
    }));
  }, [onSelectOrg]);

  // AG Grid sees the actions column as visible at mount and emits a
  // `columnVisible` event for it; that flows into the hook's set. Shield
  // the picker badge from that so it always reflects only the columns
  // the picker can actually toggle.
  const pickerVisibleColumnIds = React.useMemo(() => {
    const next = new Set(table.visibleColumnIds);
    next.delete(ACTIONS_COLUMN_ID);
    return next;
  }, [table.visibleColumnIds]);

  const orgsQuery = useListOrgs(
    { search, tier },
    table.currentPage,
    table.pageSize,
  );

  // Tier counts are computed independently of the user's tier filter so the
  // chips always show the Free/Premium breakdown of the *current name
  // search*. Two lightweight `limit: 1` queries piggy-back on the same
  // backend with `total_count` doing the work.
  const freeCountQuery = useListOrgs(
    { search, tier: OrganizationTier.Free },
    0,
    1,
  );
  const premiumCountQuery = useListOrgs(
    { search, tier: OrganizationTier.Premium },
    0,
    1,
  );

  const freeCount = freeCountQuery.totalCount;
  const premiumCount = premiumCountQuery.totalCount;
  const totalCount =
    freeCount !== undefined && premiumCount !== undefined
      ? freeCount + premiumCount
      : undefined;

  let loading: DataTableLoadingState = "idle";
  if (orgsQuery.isLoading) {
    loading = "foreground";
  } else if (orgsQuery.isFetching) {
    loading = "background";
  }

  const rows = orgsQuery.data ?? [];

  return (
    <div className={styles.container}>
      <div className={styles.summary}>
        <SummaryChip label="Total orgs" count={totalCount} variant="total" />
        <SummaryChip
          label="Free"
          count={freeCount}
          active={tier === OrganizationTier.Free}
          onClick={() => handleTierToggle(OrganizationTier.Free)}
        />
        <SummaryChip
          label="Premium"
          count={premiumCount}
          variant="premium"
          active={tier === OrganizationTier.Premium}
          onClick={() => handleTierToggle(OrganizationTier.Premium)}
        />
      </div>

      {orgsQuery.error ? (
        <Alert severity="error" className={styles.errorBanner}>
          {orgsQuery.error.message}
        </Alert>
      ) : null}

      <ResourceView.Root>
        <ResourceView.Actions>
          {/*
            Name filtering is the table's only text query, so it's a single
            search box rather than the structured-filter menu: type a name and
            press Enter to commit it. An empty submit clears the search.
          */}
          <form
            role="search"
            className={styles.search}
            onSubmit={handleSearchSubmit}
          >
            <input
              type="search"
              className={styles.searchInput}
              placeholder="Search by name…"
              aria-label="Search orgs by name"
              value={searchDraft}
              onChange={handleSearchChange}
            />
          </form>
          <ColumnVisibility
            options={COLUMN_PICKER_OPTIONS}
            visibleColumnIds={pickerVisibleColumnIds}
            setColumnVisibility={table.setColumnVisibility}
          />
        </ResourceView.Actions>
        <DataTable<OrgRecord>
          table={table}
          hasNextPage={orgsQuery.hasNextPage}
          loading={loading}
          columns={columns}
          data={rows}
          noRowsContent="No orgs match the current filters"
          totalRowCount={orgsQuery.totalCount}
        />
      </ResourceView.Root>
    </div>
  );
}
